import React from 'react';
import { motion } from 'framer-motion';
import { PartnerPortalLayout } from './PartnerPortalLayout';
import { DollarSignIcon, TargetIcon, TrendingUpIcon, UsersIcon, ChevronRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function PartnerOverviewPage() {
  const kpis = [{
    label: 'Upsell Revenue (YTD)',
    value: '$850,000',
    trend: '↗ 28%',
    trendUp: true,
    icon: DollarSignIcon,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600'
  }, {
    label: 'Identified Pipeline',
    value: '$210,000',
    icon: TargetIcon,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600'
  }, {
    label: 'Avg. Upgrade Value',
    value: '$32,500',
    trend: '↗ 4.2%',
    trendUp: true,
    icon: TrendingUpIcon,
    iconBg: 'bg-violet-50',
    iconColor: 'text-violet-600'
  }, {
    label: 'Upsell Conversion',
    value: '22%',
    icon: UsersIcon,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600'
  }];
  const highProbabilityUpsells = [{
    client: 'Aurora Consumer',
    fit: 82,
    event: 'Innovation Week',
    date: 'Jan 10 - 14, 2027',
    lift: '+$55,000',
    recommended: 'AI Leadership Retreat'
  }, {
    client: 'Helix Health',
    fit: 95,
    event: 'Post-Merger Integration',
    date: 'Sep 20 - 22, 2026',
    lift: '+$25,000',
    recommended: 'Executive Alignment Retreat'
  }, {
    client: 'NorthBridge Tech',
    fit: 92,
    event: 'Q4 Strategy Offsite',
    date: 'Nov 02 - 04, 2026',
    lift: '+$28,000',
    recommended: 'Executive Alignment Retreat'
  }];
  const pipelineHealth = [{
    label: 'Eligible Groups',
    count: 12,
    progress: 100
  }, {
    label: 'Upsell Identified',
    count: 8,
    progress: 66
  }, {
    label: 'Proposal Sent',
    count: 3,
    progress: 25
  }, {
    label: 'In Discussion',
    count: 2,
    progress: 16
  }, {
    label: 'Closed-Won',
    count: 4,
    progress: 33
  }];
  return <PartnerPortalLayout data-id="element-4985">
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-id="element-4986">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-8" data-id="element-4987">
          <h1 className="font-serif-display text-3xl md:text-4xl font-bold text-[#0B1B3B] mb-2" data-id="element-4988">
            Commercial Overview
          </h1>
          <p className="text-slate-500" data-id="element-4989">
            Track your retreat upsell performance and pipeline health.
          </p>
        </motion.div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8" data-id="element-4990">
          {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return <motion.div key={kpi.label} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-id="element-4991">
                <div className="flex items-start justify-between mb-8" data-id="element-4992">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${kpi.iconBg} ${kpi.iconColor}`} data-id="element-4993">
                    <Icon className="w-5 h-5" data-id="element-4994" />
                  </div>
                  {kpi.trend && <div className={`text-xs font-bold px-2 py-1 rounded-md ${kpi.trendUp ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-id="element-4995">
                      {kpi.trend}
                    </div>}
                </div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-4996">
                  {kpi.label}
                </div>
                <div className="font-serif-display text-3xl font-bold text-[#0B1B3B]" data-id="element-4997">
                  {kpi.value}
                </div>
              </motion.div>;
        })}
        </div>

        <div className="grid lg:grid-cols-3 gap-6" data-id="element-4998">
          {/* High-Probability Upsells */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-id="element-4999">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50" data-id="element-5000">
              <div className="flex items-center gap-2" data-id="element-5001">
                <TrendingUpIcon className="w-5 h-5 text-amber-500" data-id="element-5002" />
                <h2 className="font-serif-display text-xl font-bold text-[#0B1B3B]" data-id="element-5003">
                  High-Probability Upsells
                </h2>
              </div>
              <Link to="/partner-portal/book-analyzer" className="text-sm font-medium text-[#0B1B3B] hover:text-amber-600 flex items-center gap-1 transition-colors" data-id="element-5004">
                Open Analyzer <ChevronRightIcon className="w-4 h-4" data-id="element-5005" />
              </Link>
            </div>
            <div className="divide-y divide-slate-100 flex-1" data-id="element-5006">
              {highProbabilityUpsells.map((upsell, i) => <div key={i} className="p-6 hover:bg-slate-50 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-5007">
                  <div data-id="element-5008">
                    <div className="flex items-center gap-3 mb-1" data-id="element-5009">
                      <h3 className="font-serif-display text-xl font-bold text-[#0B1B3B]" data-id="element-5010">
                        {upsell.client}
                      </h3>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${upsell.fit >= 90 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-5011">
                        {upsell.fit} Fit
                      </span>
                    </div>
                    <div className="text-sm text-slate-500 mb-2" data-id="element-5012">
                      {upsell.event} • {upsell.date}
                    </div>
                    <div className="inline-flex items-center text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded" data-id="element-5013">
                      Recommended: {upsell.recommended}
                    </div>
                  </div>
                  <div className="text-right shrink-0" data-id="element-5014">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-5015">
                      Est. Lift
                    </div>
                    <div className="text-xl font-bold text-emerald-600" data-id="element-5016">
                      {upsell.lift}
                    </div>
                  </div>
                </div>)}
            </div>
          </motion.div>

          {/* Pipeline Health */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-id="element-5017">
            <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50" data-id="element-5018">
              <h2 className="font-serif-display text-xl font-bold text-[#0B1B3B]" data-id="element-5019">
                Pipeline Health
              </h2>
            </div>
            <div className="p-6 space-y-6 flex-1" data-id="element-5020">
              {pipelineHealth.map((item, i) => <div key={i} data-id="element-5021">
                  <div className="flex items-center justify-between mb-2" data-id="element-5022">
                    <span className="text-sm font-medium text-[#0B1B3B]" data-id="element-5023">
                      {item.label}
                    </span>
                    <span className="text-sm font-bold text-[#0B1B3B]" data-id="element-5024">
                      {item.count}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-5025">
                    <div className={`h-full rounded-full ${i === 0 ? 'bg-slate-400' : 'bg-[#0B1B3B]'}`} style={{
                  width: `${item.progress}%`
                }} data-id="element-5026" />
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </PartnerPortalLayout>;
}